import { _js } from '@ifixit/utils/localize';
import { useDebouncedValue } from '@ifixit/ui/hooks/use-debounced-value';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiClient } from 'Shared/api_client';

/** One row in a SuggestInput dropdown. */
export type Suggestion = {
   key: number | string;
   label: string;
   imageUrl: string | null;
   /**
    * The wiki title behind the row, when the label is a display title that
    * differs from it. Endpoints that resolve a wiki by title need this one,
    * not the label the picker shows.
    */
   wikiTitle?: string;
   /**
    * The wiki page behind the row. Pickers that link a picked row need this
    * one: the label is a display title, and deriving a slug from it breaks on
    * titles the wiki rejects ("Phillips #0 Screwdriver").
    */
   wikiUrl?: string;
   /**
    * A second line under the label, for pickers whose rows are ambiguous
    * without one (a guide's device, a wiki's namespace).
    */
   description?: string;
};

const DEBOUNCE_MS = 200;

/**
 * Debounced type-ahead for SuggestInput, backed by react-query so stale
 * responses cancel themselves and results dedup across renders instead of the
 * hand-rolled effect it replaces. `keyPrefix` separates the cache per picker so
 * two inputs (tags, users, ...) with the same typed text don't share results.
 */
export function useSuggestions(
   keyPrefix: string,
   query: string,
   fetchSuggestions: (query: string) => Promise<Suggestion[]>,
   minQueryLength: number,
   searchKey?: string | number | boolean
) {
   const debounced = useDebouncedValue(query.trim(), DEBOUNCE_MS);
   const enabled = debounced.length >= minQueryLength;
   const { data, isFetching, isError } = useQuery({
      queryKey: ['editor-suggest', keyPrefix, searchKey, debounced],
      queryFn: () => fetchSuggestions(debounced),
      enabled,
      // Keep the last results on screen through the next debounced fetch so
      // the dropdown doesn't flicker empty on every keystroke.
      placeholderData: keepPreviousData,
      // Stops a refetch on window refocus and dedups the backspace case
      // (deleting back to an already-fetched prefix reuses that result).
      staleTime: 30_000,
   });

   return {
      suggestions: enabled ? (data ?? []) : [],
      isSearching: enabled && isFetching,
      error: enabled && isError ? _js('Something went wrong searching. Please try again.') : null,
   };
}

type ApiRecord = Record<string, any>;

function firstImage(record: ApiRecord): string | null {
   return record?.image?.mini ?? record?.imageUrl ?? null;
}

/**
 * Tool/part name suggestions from the ITEM-namespace wiki index, so typed
 * names line up with real item pages instead of freehand spellings.
 */
export async function suggestItems(query: string): Promise<Suggestion[]> {
   const response = await apiClient.get(`suggest/${encodeURIComponent(query)}?doctypes=item`);
   const results: ApiRecord[] = response.data?.results ?? [];
   return results.map(result => ({
      key: result.wikiid,
      label: result.display_title || result.title,
      imageUrl: firstImage(result),
      wikiTitle: result.title,
      wikiUrl: result.url,
   }));
}

/**
 * Guide search for the prerequisites picker. Prereq-only and unreleased
 * guides are included (they don't appear in regular browse but are valid
 * prereqs), and `guideid:123` works as a direct-id shortcut. Passing a
 * device scopes the search to that device.
 */
export async function suggestGuides(query: string, device?: string): Promise<Suggestion[]> {
   const params = new URLSearchParams({
      doctypes: 'guide',
      includePrereqOnlyGuides: '1',
      includeUnreleased: '1',
   });
   if (device) {
      params.set('guideDevice', device);
   }
   const response = await apiClient.get(`suggest/${encodeURIComponent(query)}?${params}`);
   const results: ApiRecord[] = response.data?.results ?? [];
   return results
      .filter(result => result.dataType === 'guide')
      .map(result => ({
         key: result.guideid,
         label: result.title,
         imageUrl: firstImage(result),
      }));
}

/** Device/topic category suggestions from the TOPIC wiki index. */
export async function suggestTopics(query: string): Promise<Suggestion[]> {
   const response = await apiClient.get(`suggest/${encodeURIComponent(query)}?doctypes=topic`);
   const results: ApiRecord[] = response.data?.results ?? [];
   return results.map(result => ({
      key: result.wikiid,
      label: result.display_title || result.title,
      imageUrl: firstImage(result),
      wikiTitle: result.title,
   }));
}

export async function searchUsers(query: string): Promise<Suggestion[]> {
   const response = await apiClient.get(`users/search/${encodeURIComponent(query)}`);
   const results: ApiRecord[] = response.data?.results ?? [];
   return results.map(user => ({
      key: user.userid,
      label: user.unique_username ? `${user.username} (@${user.unique_username})` : user.username,
      imageUrl: firstImage(user),
   }));
}

export async function searchTeams(query: string): Promise<Suggestion[]> {
   const response = await apiClient.get(
      `suggest/usersAndTeams/${encodeURIComponent(query)}?userLimit=1&teamLimit=25`
   );
   const teams: ApiRecord[] = response.data?.teams ?? [];
   return teams.map(team => ({
      key: team.teamid,
      label: team.name,
      imageUrl: firstImage(team),
   }));
}

/**
 * Existing-tag suggestions for the tag editors, across all doctypes so a
 * wiki tagger sees guide tags too.
 */
export async function suggestTags(query: string): Promise<Suggestion[]> {
   const response = await apiClient.get(`suggest/tags/${encodeURIComponent(query)}?doctypes=all`);
   const results: string[] = response.data?.results ?? [];
   return results.map(tag => ({ key: tag, label: tag, imageUrl: null }));
}
